// ===========================================
// Report settings — Email reports tab
// ===========================================
// Scheduled digests: which admins, how often, which reports, PDF attached.
//
// ⚠️ `sendHour` is the SCHOOL's local hour, not the viewer's. The sweep runs
// hourly in UTC and matches each school's own clock, which is what makes a
// "daily at 8am" digest arrive at 8am all year rather than drifting an hour
// across DST. The UI says so, because an admin in another timezone would
// otherwise reasonably read it as their own.

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Send as SendIcon } from '@mui/icons-material';
import {
  useReportSchedules,
  useDigestRecipients,
  useCreateReportSchedule,
  useUpdateReportSchedule,
  useDeleteReportSchedule,
  useSendReportScheduleTest,
} from '../../../hooks/useAdminSettings';
import { REPORT_KEYS, REPORT_LABELS, type ReportKey } from '../../../constants/reportKeys.constants';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import type {
  ReportSchedule,
  ReportScheduleFrequency,
} from '../../../types/adminSettings.types';

const FREQUENCIES: Array<{ value: ReportScheduleFrequency; label: string; window: string }> = [
  { value: 'DAILY', label: 'Daily', window: 'covers the previous day' },
  { value: 'WEEKLY', label: 'Weekly', window: 'covers the previous 7 days' },
  { value: 'MONTHLY', label: 'Monthly', window: 'covers the previous calendar month' },
];

/** 0=Sunday … 6=Saturday, matching JS `getDay()` and the backend's sendDay. */
const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

/**
 * ⚠️ Stops at 28, not 31. A schedule set to the 29th, 30th or 31st would
 * silently never fire in a shorter month. "Last day of the month" would be a
 * separate option, not a bigger number.
 */
const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

/** "1st", "2nd", "3rd", "21st" … for the day-of-month picker and the caption. */
function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th';
  return `${n}${suffix}`;
}

/**
 * Keep `sendDay` inside the range its frequency allows. Mirrors the backend's
 * `clampSendDay` so the form can never submit a pair the validator refuses.
 */
function clampDay(frequency: ReportScheduleFrequency, sendDay: number): number {
  if (frequency === 'WEEKLY') return Math.min(6, Math.max(0, sendDay));
  if (frequency === 'MONTHLY') return Math.min(28, Math.max(1, sendDay));
  return 1; // DAILY — unused, normalised
}

/** "Weekly on Mondays" / "Monthly on the 1st" / "Daily". */
function scheduleWhen(frequency: ReportScheduleFrequency, sendDay: number): string {
  if (frequency === 'WEEKLY') {
    const day = WEEKDAYS.find((d) => d.value === sendDay)?.label ?? 'Monday';
    return `Weekly on ${day}s`;
  }
  if (frequency === 'MONTHLY') return `Monthly on the ${ordinal(sendDay)}`;
  return 'Daily';
}

interface FormState {
  name: string;
  frequency: ReportScheduleFrequency;
  sendHour: number;
  sendDay: number;
  reportKeys: ReportKey[];
  recipientUserIds: string[];
  includeSummary: boolean;
  isActive: boolean;
}

const emptyForm = (): FormState => ({
  name: '',
  frequency: 'WEEKLY',
  sendHour: 8,
  sendDay: 1, // Monday — the default an admin who never chooses would expect

  reportKeys: ['attendance', 'revenue'],
  recipientUserIds: [],
  includeSummary: true,
  isActive: true,
});

function formFrom(schedule: ReportSchedule): FormState {
  return {
    name: schedule.name,
    frequency: schedule.frequency,
    sendHour: schedule.sendHour,
    sendDay: schedule.sendDay,
    reportKeys: schedule.reportKeys,
    recipientUserIds: schedule.recipients.map((r) => r.userId),
    includeSummary: schedule.includeSummary,
    isActive: schedule.isActive,
  };
}

/** dd/mm/yyyy — Australian order (CLAUDE.md), never a bare ISO string. */
function formatSent(iso: string | null): string {
  if (!iso) return 'Never sent';
  return `Last sent ${new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso))}`;
}

/** The owner's report (roles slice 2, V25): a schedule carrying it is the owner's. */
const RETRO_EDITS: ReportKey = 'retro-edits';

export default function ReportSchedulesTab() {
  const { data: schedules, isLoading, isError, error } = useReportSchedules();
  const { data: recipients } = useDigestRecipients();
  // The Retro Edits report is the owner's (roles slice 2, V25): a schedule that includes it goes to
  // Admins only, and only an Admin may add it, change, test or delete such a schedule. The server
  // refuses all of it; this says so before anyone tries.
  const { isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const createMutation = useCreateReportSchedule();
  const updateMutation = useUpdateReportSchedule();
  const deleteMutation = useDeleteReportSchedule();
  const testMutation = useSendReportScheduleTest();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReportSchedule | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  useEffect(() => {
    if (!dialogOpen) return;
    setForm(editing ? formFrom(editing) : emptyForm());
  }, [dialogOpen, editing]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (schedule: ReportSchedule) => {
    setEditing(schedule);
    setDialogOpen(true);
  };

  const handleSave = () => {
    const payload = {
      name: form.name.trim(),
      frequency: form.frequency,
      sendHour: form.sendHour,
      sendDay: form.sendDay,
      reportKeys: form.reportKeys,
      recipientUserIds: form.recipientUserIds,
      includeSummary: form.includeSummary,
      isActive: form.isActive,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: payload }, { onSuccess: () => setDialogOpen(false) });
    } else {
      createMutation.mutate(payload, { onSuccess: () => setDialogOpen(false) });
    }
  };

  const handleDelete = (schedule: ReportSchedule) => {
    if (window.confirm(`Delete "${schedule.name}"? This cannot be undone.`)) {
      deleteMutation.mutate(schedule.id);
    }
  };

  const toggleKey = (key: ReportKey) => {
    setForm((f) => ({
      ...f,
      // Kept in REPORT_KEYS order so the payload is stable regardless of the
      // order the admin happened to tick things in.
      reportKeys: REPORT_KEYS.filter((k) =>
        k === key ? !f.reportKeys.includes(k) : f.reportKeys.includes(k)
      ),
    }));
  };

  const toggleRecipient = (userId: string) => {
    setForm((f) => ({
      ...f,
      recipientUserIds: f.recipientUserIds.includes(userId)
        ? f.recipientUserIds.filter((id) => id !== userId)
        : [...f.recipientUserIds, userId],
    }));
  };

  // Stored recipients the picker no longer offers (demoted, role removed or expired, deactivated).
  // Listed so they can be unticked: a save normally takes them off and names them, but one that
  // ADDS Retro Edits is refused over them, and the form must be able to clear that (step 9 /qa L2).
  const offeredIds = new Set((recipients ?? []).map((r) => r.userId));
  const notOffered = editing ? editing.recipients.filter((r) => !offeredIds.has(r.userId)) : [];
  const formIsOwnerOnly = form.reportKeys.includes(RETRO_EDITS);
  const addingRetroEdits = formIsOwnerOnly && !!editing && !editing.reportKeys.includes(RETRO_EDITS);
  const staffOnOwnerOnly = formIsOwnerOnly
    ? [
        ...(recipients ?? []).filter((r) => r.tier === 'STAFF' && form.recipientUserIds.includes(r.userId)),
        ...(addingRetroEdits ? notOffered.filter((r) => form.recipientUserIds.includes(r.userId)) : []),
      ]
    : [];

  const saveDisabled =
    staffOnOwnerOnly.length > 0 ||
    (!isOwner && !!editing?.reportKeys.includes(RETRO_EDITS)) ||
    form.name.trim().length === 0 ||
    form.recipientUserIds.length === 0 ||
    form.reportKeys.length === 0 ||
    createMutation.isPending ||
    updateMutation.isPending;

  // Three states, never two — a failed load must not read as "no schedules".
  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={120} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Failed to load report schedules{error instanceof Error ? `: ${error.message}` : '.'}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Digests are emailed to the people you choose, with the selected reports attached as one PDF.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New schedule
        </Button>
      </Stack>

      {schedules && schedules.length === 0 && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="h6" gutterBottom>
              No schedules yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create one to have reports emailed automatically.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2}>
        {schedules?.map((schedule) => (
          <Card key={schedule.id} variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="h6">{schedule.name}</Typography>
                    {!schedule.isActive && <Chip size="small" label="Paused" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {scheduleWhen(schedule.frequency, schedule.sendDay)} at{' '}
                    {String(schedule.sendHour).padStart(2, '0')}:00 school time ·{' '}
                    {formatSent(schedule.lastSentAt)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {schedule.reportKeys.length} report
                    {schedule.reportKeys.length === 1 ? '' : 's'} ·{' '}
                    {schedule.recipients.length} recipient
                    {schedule.recipients.length === 1 ? '' : 's'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => testMutation.mutate(schedule.id)}
                    disabled={testMutation.isPending || (!isOwner && schedule.reportKeys.includes(RETRO_EDITS))}
                  >
                    Send test
                  </Button>
                  <Button
                    size="small"
                    onClick={() => openEdit(schedule)}
                    disabled={!isOwner && schedule.reportKeys.includes(RETRO_EDITS)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDelete(schedule)}
                    disabled={!isOwner && schedule.reportKeys.includes(RETRO_EDITS)}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>
              {!isOwner && schedule.reportKeys.includes(RETRO_EDITS) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  This schedule includes Retro Edits, so only your {ownerTitle} can change, test or delete it.
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit schedule' : 'New schedule'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              helperText="Shown as the email subject, e.g. “Monday morning digest”"
            />

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="frequency-label">Frequency</InputLabel>
                <Select
                  labelId="frequency-label"
                  label="Frequency"
                  value={form.frequency}
                  onChange={(e) => {
                    const frequency = e.target.value as ReportScheduleFrequency;
                    // ⚠️ Clamp into the NEW frequency's range, in BOTH
                    // directions. An earlier version clamped only for MONTHLY,
                    // so Monthly-28th → Weekly kept sendDay 28: the weekday
                    // Select rendered with a value matching no MenuItem (blank),
                    // and Save returned a 400 about a field the admin could not
                    // see. Weekly→Monthly had the mirror problem, silently
                    // picking the 6th from a Saturday.
                    setForm((f) => ({ ...f, frequency, sendDay: clampDay(frequency, f.sendDay) }));
                  }}
                >
                  {FREQUENCIES.map((f) => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="hour-label">Send at</InputLabel>
                <Select
                  labelId="hour-label"
                  label="Send at"
                  value={form.sendHour}
                  onChange={(e) => setForm((f) => ({ ...f, sendHour: Number(e.target.value) }))}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <MenuItem key={h} value={h}>
                      {String(h).padStart(2, '0')}:00
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* The day picker appears only where a day means something — a
                daily digest has no day to choose. */}
            {form.frequency === 'WEEKLY' && (
              <FormControl fullWidth>
                <InputLabel id="weekday-label">Send on</InputLabel>
                <Select
                  labelId="weekday-label"
                  label="Send on"
                  value={form.sendDay}
                  onChange={(e) => setForm((f) => ({ ...f, sendDay: Number(e.target.value) }))}
                >
                  {WEEKDAYS.map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {form.frequency === 'MONTHLY' && (
              <FormControl fullWidth>
                <InputLabel id="monthday-label">Send on the</InputLabel>
                <Select
                  labelId="monthday-label"
                  label="Send on the"
                  value={form.sendDay}
                  onChange={(e) => setForm((f) => ({ ...f, sendDay: Number(e.target.value) }))}
                >
                  {MONTH_DAYS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {ordinal(d)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Stops at the 28th so the digest never skips a shorter month.
                </FormHelperText>
              </FormControl>
            )}

            <Alert severity="info" sx={{ mt: -1 }}>
              {scheduleWhen(form.frequency, form.sendDay)} at{' '}
              {String(form.sendHour).padStart(2, '0')}:00 in your school's timezone, and{' '}
              {FREQUENCIES.find((f) => f.value === form.frequency)?.window}. The first digest arrives
              on the next matching day — use “Send test” to see one now. Term-based reports show
              their own term and say so.
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Reports to include
              </Typography>
              <List dense disablePadding>
                {REPORT_KEYS.filter((k) => k !== 'email-log').map((key) => (
                  <ListItem key={key} disableGutters sx={{ py: 0 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.reportKeys.includes(key)}
                          onChange={() => toggleKey(key)}
                          disabled={key === RETRO_EDITS && !isOwner}
                        />
                      }
                      label={
                        key === RETRO_EDITS
                          ? `${REPORT_LABELS[key]} (sent to your ${ownerTitle} logins only)`
                          : REPORT_LABELS[key]
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Send to
              </Typography>
              {recipients && recipients.length === 0 && (
                <Alert severity="warning">No one in this school can receive reports yet.</Alert>
              )}
              {staffOnOwnerOnly.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Retro Edits goes to your {ownerTitle} logins only. Untick{' '}
                  {staffOnOwnerOnly.map((r) => `${r.firstName} ${r.lastName}`).join(', ')}, or take Retro Edits out
                  of this schedule.
                </Alert>
              )}
              <List dense disablePadding>
                {recipients?.map((r) => (
                  <ListItem key={r.userId} disableGutters sx={{ py: 0 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.recipientUserIds.includes(r.userId)}
                          onChange={() => toggleRecipient(r.userId)}
                        />
                      }
                      label={
                        <ListItemText
                          primary={`${r.firstName} ${r.lastName}`}
                          secondary={r.tier === 'STAFF' ? `Staff · ${r.email}` : r.email}
                          sx={{ my: 0 }}
                        />
                      }
                    />
                  </ListItem>
                ))}
                {notOffered.map((r) => (
                  <ListItem key={r.userId} disableGutters sx={{ py: 0 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.recipientUserIds.includes(r.userId)}
                          onChange={() => toggleRecipient(r.userId)}
                        />
                      }
                      label={
                        <ListItemText
                          primary={`${r.firstName} ${r.lastName}`}
                          secondary={`Can no longer receive reports · ${r.email}`}
                          sx={{ my: 0 }}
                        />
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={form.includeSummary}
                  onChange={(e) => setForm((f) => ({ ...f, includeSummary: e.target.checked }))}
                />
              }
              label="Include a summary in the email body"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveDisabled}>
            {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
